import { IoAdd, IoMailOutline, IoMailOpenOutline } from "react-icons/io5";
import { useCallback, useEffect, useState, useRef } from "react";
import { getRoomsAsync } from "../../../services/chatService";
import Toaster from "./../../../components/common/Toaster";
import Modal from "./../../../components/common/Modal";
import NewMessage from "./NewMessage";
import authService from "./../../../services/authService";
import moment from "moment";
import _ from "lodash";

const user = authService.getCurrentUser();

const Rooms = ({ room, rooms, setRooms, joinRoom }) => {
  const [previousRooms, setPreviousRooms] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [response, setResponse] = useState([]);
  const [data, setData] = useState([]);

  const observer = useRef();

  const fetchRooms = useCallback(
    async (mounted) => {
      try {
        setIsLoading(true);
        const result = await getRoomsAsync(user.user_id, page);
        if (mounted) {
          setResponse(result);
          setIsLoading(false);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [page]
  );

  const handleRooms = useCallback(() => {
    const { result, count, total_count } = response;
    const union = _.union(previousRooms, result);
    const list = Object.assign([], union);

    setRooms(list);
    setHasMore(previousRooms.length + count < total_count);
  }, [response, previousRooms, setRooms]);

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
    setPreviousRooms(rooms);
  }, [rooms]);

  const loaderRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          handleLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, handleLoadMore]
  );

  useEffect(() => {
    let mounted = true;

    fetchRooms(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchRooms]);

  useEffect(() => {
    handleRooms();
  }, [handleRooms]);

  useEffect(() => {
    const sorted = _.orderBy(rooms, "created_on", ["desc"]);
    setData(sorted);
  }, [rooms]);

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex justify-between p-4 border-b lg:p-3">
        <div className="text-gray-400 hidden lg:block">Inbox</div>
        <div
          className="text-blue-400 flex cursor-pointer"
          onClick={() => setShowForm(true)}
        >
          <IoMailOutline className="text-lg" />
          <IoAdd className="text-sm" />
        </div>
      </div>
      <div className="overflow-y-auto scrollbar">
        {rooms.length === 0 && (
          <div className="p-5 hidden lg:block">
            <IoMailOpenOutline className="text-gray-300 mb-3 text-3xl mx-auto" />
            <p className="text-gray-400 mx-5 text-sm text-center">
              You don't have any messages
            </p>
          </div>
        )}
        {data.map((item, index) => {
          return (
            <div
              key={index}
              onClick={() => joinRoom(item)}
              className={`p-4 cursor-pointer border-gray-100 border-b last:border-b-0 hover:bg-gray-50 ${
                room && room.id === item.id && `bg-blue-50`
              }`}
            >
              <div className="flex justify-between">
                <div className="flex items-center">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-gray-300">
                    <span className="text-white text-sm">
                      {item.name.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-4 hidden lg:block">
                    <div className="text-gray-500 text-sm overflow-hidden truncate w-40">
                      {item.name}
                    </div>
                    <div className="text-gray-400 text-xs overflow-hidden truncate w-40">
                      {item.text_message}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400 hidden lg:block">
                  {moment(item.created_on).fromNow(true)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={loaderRef}></div>
      </div>
      <Modal
        title="New Message"
        isOpen={showForm}
        isLoading={isFormLoading}
        onClose={() => setShowForm(false)}
        borderTop
      >
        <NewMessage
          rooms={rooms}
          isLoading={isFormLoading}
          setIsLoading={setIsFormLoading}
          onSelect={joinRoom}
          onClose={() => setShowForm(false)}
        />
      </Modal>
    </div>
  );
};

export default Rooms;
